/**
 * The question cards: one question at a time, and they advance on their own.
 *
 * These are the questions only a person settles: a fact, an unmarked question,
 * one with no recommendation, or a decision that cannot be undone. A decision
 * Reeve has a recommendation for is not asked here; it is stated above the
 * cards (`StatedDecisions`) and approving accepts it.
 *
 * Picking an offered answer fills its radio, holds for a beat (240 ms) so the
 * choice is seen, then slides the card out to the left and the next one in
 * from the right. Each option shows its one-line reason and, when it has one,
 * what picking it changes in the plan. Skip takes the recommendation when Skip
 * may settle the question (`skipChooses`), and the approval stores that as
 * Reeve's choice, not the reader's; on a fact, a one-way decision or a question
 * with no recommendation, Skip leaves it open. Only "Something else" waits,
 * because the reader is typing; Next saves it and moves on. Reduced motion
 * gets a plain swap.
 *
 * Each answer is saved on the server as it is given, and Reeve writes it into
 * the plan as a new version (spec 2026-11-02). An answered question stays
 * listed above the cards (`PlanAnswerLines`): its answer, where it stands in
 * the plan, Change (its card opens again) and Undo.
 *
 * A question carries its mark when the plan made one: FACT (it could be looked
 * up) or DECISION (a person's call), and CAN'T BE UNDONE on a one-way decision.
 * A fact shows what Reeve read for it and what it could not, and, when the
 * Team lacks the connected tool that would answer it, a way to add that tool
 * (a reader who may change the Team) or to ask the owner to (everyone else).
 *
 * `AskCard` is the same card for a run that stopped to ask: its question is
 * prose, so it has no options, only the answer the reader writes.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  getConnectorCatalogEntry,
  skipChooses,
  type GateAskLink,
  type PlanInputView,
  type PlanQuestionOption,
} from "@yagni/shared";
import type { PlanQuestionRow } from "../work/planQuestions.js";
import { planChangeLine } from "../work/workCopy.js";
import { PlanAnswerLines } from "./PlanAnswerLines.js";
import { Btn, Stamp, cx } from "../queue/index.js";
import { Textarea } from "../ui/textarea.js";
import { AskLink } from "../work/AskFacts.js";
import { SOURCE_SHAPE_COPY, sourceShapeFor } from "../teams/people/sourceShape.js";

/** How long a picked answer holds before the card leaves. */
export const PICK_HOLD_MS = 240;
/** How long the card takes to leave (`.qcard.leave` in queue.css). */
export const LEAVE_MS = 180;

/**
 * A written answer takes several lines: the option row's own chrome, the
 * textarea bare inside it (`.opt.other textarea` in queue.css sizes it).
 */
export const OPTION_TEXTAREA =
  "border-0 bg-transparent rounded-none p-0 resize-none focus-visible:ring-0 focus-visible:outline-none";

function reducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

function optionsOf(question: PlanQuestionRow): Array<PlanQuestionOption & { rec: boolean }> {
  return [
    ...(question.recommendation ? [{ ...question.recommendation, rec: true }] : []),
    ...question.alternatives.map((option) => ({ ...option, rec: false })),
  ];
}

/** An option's answer, its one-line reason, and what picking it changes in the plan. */
export function OptionText({ option }: { option: PlanQuestionOption }) {
  const why = option.why?.trim();
  const change = planChangeLine(option.planChange);
  return (
    <span className="ot">
      <span>{option.answer}</span>
      {why && <span className="ow">{why}</span>}
      {change && (
        <span className="oc" data-testid="qcard-option-change">
          {change}
        </span>
      )}
    </span>
  );
}

/** A progress tick: answered, the one on screen, or neither. */
function tickClass(answered: boolean, current: boolean): string | undefined {
  if (answered) return "ok";
  return current ? "on" : undefined;
}

function Asker({ name }: { name: string }) {
  return (
    <span className="who">
      <span className="dot" aria-hidden />
      {name} asks
    </span>
  );
}

/** A connected tool's name as the Connections page prints it ("Database"). */
export function sourceLabel(source: string): string {
  return getConnectorCatalogEntry(source)?.label ?? source;
}

/** What the cards may do about a fact's missing connected tool. */
export interface QuestionToolActions {
  /** May the reader change the Team's connected tools? The server's word. */
  canManage: boolean;
  /**
   * The sources hidden from the Team (YAG-986). Every workspace connection
   * reaches a Team by default, so a fact's missing source is either one of
   * these (shown again with Add's route) or not connected in the workspace
   * at all (connected on Connections). Absent: the older "add" shape.
   */
  hiddenSources?: readonly string[];
  /** Where an admin connects a source the workspace lacks; null for everyone else. */
  connectUrl?: string | null;
  /** Sources added from these cards during this visit. */
  added: readonly string[];
  /** The source being added right now. */
  adding: string | null;
  /** The last Add the server refused, in its own sentence. */
  failed: { source: string; message: string } | null;
  onAdd: (source: string) => void;
  /** The ask-the-owner verb's label, for a reader who cannot add it. */
  askLabel: (label: string, source: string) => string;
  askDisabled: (source: string) => boolean;
  onAskOwner: (source: string, label: string) => void;
}

/** What Reeve read for a fact, and what it could not. */
export function CheckedLines({
  question,
  askerName,
}: {
  question: PlanQuestionRow;
  askerName: string;
}) {
  if (!question.checked?.length) return null;
  return (
    <div className="qcheck" data-testid="qcard-checked">
      <span className="label">What {askerName} checked</span>
      <div className="checks">
        {question.checked.map((line, k) => (
          <div key={k}>
            <span className={line.status === "checked" ? "ok" : "note"}>
              {line.status === "checked" ? "Checked" : "Could not check"}
            </span>
            <span>{line.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A fact the Team's connected tools cannot answer: add the tool, or ask the owner to. */
function MissingTool({
  source,
  askerName,
  actions,
}: {
  source: string;
  askerName: string;
  actions: QuestionToolActions | undefined;
}) {
  const label = sourceLabel(source);
  if (actions?.added.includes(source)) {
    return (
      <div className="qtool added" data-testid="qcard-tool-added">
        <Stamp>{SOURCE_SHAPE_COPY[sourceShapeFor(actions.hiddenSources, source)].stamp}</Stamp>
        <span className="t">
          <b>{label} is now one of this Team&rsquo;s connected tools.</b>
          <span>Send back to let {askerName} check it, or answer it yourself below.</span>
        </span>
      </div>
    );
  }
  const failed = actions?.failed?.source === source ? actions.failed.message : null;
  // Every workspace connection reaches a Team by default (YAG-986): the
  // source is hidden from this Team, or not connected in the workspace.
  // Without a hidden list (an older caller) the card keeps its "add" shape.
  const shape = sourceShapeFor(actions?.hiddenSources, source);
  const copy = SOURCE_SHAPE_COPY[shape];
  // Who may act: an admin connects on Connections; a manager shows (or adds);
  // everyone else asks the owner.
  const connectHere = shape === "connect" && Boolean(actions?.connectUrl);
  const showHere = shape !== "connect" && Boolean(actions?.canManage);
  return (
    <div className="qtool" data-testid="qcard-tool" data-shape={shape}>
      <span>{copy.couldCheck(askerName, label)}</span>
      {connectHere && actions ? (
        <a
          className="btn secondary sm"
          href={actions.connectUrl ?? "#"}
          data-testid="qcard-tool-connect"
        >
          {copy.verb(label)}
        </a>
      ) : showHere && actions ? (
        <Btn
          variant="secondary"
          size="sm"
          disabled={actions.adding != null}
          onClick={() => actions.onAdd(source)}
        >
          {actions.adding === source ? copy.busy(label) : copy.verb(label)}
        </Btn>
      ) : actions ? (
        <Btn
          variant="secondary"
          size="sm"
          disabled={actions.askDisabled(source)}
          onClick={() => actions.onAskOwner(source, label)}
        >
          {actions.askLabel(label, source)}
        </Btn>
      ) : null}
      {failed && (
        <span className="ask err" role="alert">
          {failed}
        </span>
      )}
    </div>
  );
}

export interface QuestionCardsProps {
  questions: readonly PlanQuestionRow[];
  /** The live answers saved on the gate, one per question at most (`isPlanAnswer`). */
  answers: readonly PlanInputView[];
  /** Save an answer. `skipped`: Skip took the recommendation, so the choice is Reeve's. */
  onAnswer: (question: PlanQuestionRow, answer: string, why: string, skipped?: boolean) => void;
  /** Undo: the answer is withdrawn, and the plan goes back to what it was without it. */
  onUndo: (questionId: string) => void;
  /** Retry an answer that could not be written into the plan. */
  onRetry: (inputId: string) => void;
  /** Start over with an answer the plan cannot take: the Start over dialog, with it as the reason. */
  onStartOver: (input: PlanInputView) => void;
  /** Keep an answer that could not be written into the plan as its decision line. */
  onKeepAsDecision?: (inputId: string) => void;
  onAsk: (question: PlanQuestionRow) => void;
  /** Who asks ("Reeve"). */
  askerName: string;
  /** The asker's colour, for the dot. */
  agentColor?: string;
  /** What a fact's missing connected tool can be done about; absent shows the line alone. */
  toolActions?: QuestionToolActions;
}

export function QuestionCards({
  questions,
  answers,
  onAnswer,
  onUndo,
  onRetry,
  onStartOver,
  onKeepAsDecision,
  onAsk,
  askerName,
  agentColor,
  toolActions,
}: QuestionCardsProps) {
  // The question Change opened again: it is a card until it is answered anew.
  const [reopened, setReopened] = useState<string | null>(null);
  const answeredAt = (question: PlanQuestionRow) =>
    answers.find((entry) => entry.questionId === question.id)?.body ?? null;
  const isOpen = (question: PlanQuestionRow) =>
    answeredAt(question) == null || question.id === reopened;
  const firstOpen = () => {
    const index = questions.findIndex(isOpen);
    return index < 0 ? questions.length : index;
  };
  const [index, setIndex] = useState(firstOpen);
  const [phase, setPhase] = useState<"idle" | "hold" | "leave" | "enter">("idle");
  // What the reader is typing in "Something else", as typed; null until they type.
  const [other, setOther] = useState<string | null>(null);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer);
    },
    [],
  );
  const key = questions.map((question) => question.id).join("|");
  // A different question set (a new revision) starts at its first open question.
  useEffect(() => {
    setReopened(null);
    setIndex(firstOpen());
    setPhase("idle");
    setOther(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const total = questions.length;
  const answered = questions.filter((question) => answeredAt(question) != null).length;
  const anyOpen = questions.some(isOpen);
  // The card on screen stays put while a pick holds and leaves, even once its
  // answer is saved; past the end with a question still open (one skipped
  // with no recommendation to fall back on), back to the first open one.
  const at = index < total ? index : Math.max(0, firstOpen());
  const question = index < total || anyOpen ? questions[at] : undefined;

  const lines = (
    <PlanAnswerLines
      questions={questions.filter((entry) => entry.id !== reopened)}
      answers={answers}
      askerName={askerName}
      onChange={(questionId) => {
        setReopened(questionId);
        setIndex(
          Math.max(
            0,
            questions.findIndex((entry) => entry.id === questionId),
          ),
        );
        setOther(null);
        setPhase(reducedMotion() ? "idle" : "enter");
      }}
      onUndo={(questionId) => {
        if (reopened === questionId) setReopened(null);
        onUndo(questionId);
      }}
      onRetry={onRetry}
      onStartOver={onStartOver}
      onKeepAsDecision={onKeepAsDecision}
    />
  );
  if (!question) {
    return (
      <div className="blk" data-testid="plan-questions">
        {lines}
      </div>
    );
  }

  const advance = () => {
    const next = () => {
      // The next question still open after this one, else the first open one, else the end.
      const after = questions.findIndex(
        (entry, k) => k > at && answeredAt(entry) == null && entry.id !== question.id,
      );
      const before = questions.findIndex(
        (entry, k) => k < at && answeredAt(entry) == null && entry.id !== question.id,
      );
      const next = [after, before].find((at) => at >= 0);
      setReopened(null);
      setIndex(next ?? total);
      setOther(null);
      setPhase(reducedMotion() ? "idle" : "enter");
    };
    if (reducedMotion()) {
      next();
      return;
    }
    setPhase("leave");
    timers.current.push(setTimeout(next, LEAVE_MS));
  };

  const pick = (option: PlanQuestionOption) => {
    if (phase === "hold" || phase === "leave") return;
    if (answeredAt(question) !== option.answer) onAnswer(question, option.answer, option.why);
    if (reducedMotion()) {
      advance();
      return;
    }
    setPhase("hold");
    timers.current.push(setTimeout(advance, PICK_HOLD_MS));
  };

  const skip = () => {
    if (question.recommendation && skipChooses(question) && answeredAt(question) == null) {
      onAnswer(question, question.recommendation.answer, question.recommendation.why, true);
    }
    advance();
  };

  const chosen = answeredAt(question);
  const options = optionsOf(question);
  const typed =
    chosen != null && !options.some((option) => option.answer === chosen) ? chosen : null;
  const written = other ?? typed ?? "";
  // Next saves a typed answer that is not the one saved, then moves on.
  const next = () => {
    if (other != null && other.trim() && other.trim() !== chosen) onAnswer(question, other, "");
    advance();
  };
  const canNext = chosen != null || (other ?? "").trim() !== "";
  const remaining = questions.filter(
    (entry) => entry.id !== question.id && answeredAt(entry) == null,
  ).length;

  return (
    <div className="blk" data-testid="plan-questions">
      {lines}
      <div
        className={cx("qcard", phase === "leave" && "leave", phase === "enter" && "enter")}
        style={agentColor ? ({ "--agent": agentColor } as CSSProperties) : undefined}
        data-testid="qcard"
        onAnimationEnd={() => {
          if (phase === "enter") setPhase("idle");
        }}
      >
        <div className="qh">
          <Asker name={askerName} />
          {question.kind && (
            <span
              className={cx("kind", question.kind === "fact" ? "fact-k" : "decision-k")}
              data-testid="qcard-kind"
            >
              {question.kind === "fact" ? "Fact" : "Decision"}
            </span>
          )}
          {question.oneWay && (
            <span className="kind oneway-k" data-testid="qcard-one-way">
              Can&rsquo;t be undone
            </span>
          )}
          <span className="fact" style={{ fontSize: 11 }}>
            {at + 1} of {total}
          </span>
          <span className="prog" aria-label={`${answered} of ${total} answered`}>
            {questions.map((entry, k) => (
              <i key={entry.id} className={tickClass(answeredAt(entry) != null, k === at)} />
            ))}
          </span>
        </div>
        <p className="q">{question.text}</p>
        <CheckedLines question={question} askerName={askerName} />
        {question.needsTool && (
          <MissingTool source={question.needsTool} askerName={askerName} actions={toolActions} />
        )}
        <div className="opts" role="radiogroup" aria-label={question.text}>
          {options.map((option) => (
            <button
              key={option.answer}
              type="button"
              role="radio"
              aria-checked={other == null && chosen === option.answer}
              className={cx("opt", other == null && chosen === option.answer && "on")}
              disabled={phase === "hold" || phase === "leave"}
              onClick={() => {
                setOther(null);
                pick(option);
              }}
            >
              <span className="rad" aria-hidden />
              <OptionText option={option} />
              {option.rec && <span className="rec">recommended</span>}
            </button>
          ))}
          <div className={cx("opt", "other", written.trim() && "on")}>
            <Textarea
              placeholder="Something else"
              aria-label="Something else"
              value={written}
              onChange={(event) => setOther(event.target.value)}
              rows={1}
              autosize
              className={OPTION_TEXTAREA}
            />
          </div>
        </div>
        <div className="qacts">
          <Btn variant="quiet" size="sm" onClick={skip}>
            Skip
          </Btn>
          <Btn variant="quiet" size="sm" onClick={() => onAsk(question)}>
            Ask YAGNI
          </Btn>
          <span className="spacer" />
          {canNext && (
            <Btn variant="secondary" size="sm" onClick={next}>
              {remaining > 0 ? "Next question" : "Done"}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

export interface AskCardProps {
  question: string;
  why?: string | null;
  askerName: string;
  answer: string;
  onAnswer: (text: string) => void;
  onAsk: () => void;
  /** An infra stop needs no answer; the note is optional context. */
  optional?: boolean;
  /** The ask's link, drawn under the need (`why`). */
  link?: GateAskLink | null;
}

/**
 * A Team's own accept that could not tell which repository the ticket builds
 * in: the question and why. The repository picker under it is the answer.
 */
export function RepoAskCard({
  question,
  why,
  askerName,
}: {
  question: string;
  why: string | null;
  askerName: string;
}) {
  return (
    <div className="qcard" data-testid="repo-askcard">
      <div className="qh">
        <Asker name={askerName} />
      </div>
      <p className="q">{question}</p>
      {why && <p className="why">{why}</p>}
    </div>
  );
}

/** A run that stopped to ask: its prose question, and the answer the reader writes. */
export function AskCard({
  question,
  why,
  askerName,
  answer,
  onAnswer,
  onAsk,
  optional,
  link,
}: AskCardProps) {
  return (
    <div className="qcard" data-testid="askcard">
      <div className="qh">
        <Asker name={askerName} />
      </div>
      <p className="q">{question}</p>
      {why && <p className="why">{why}</p>}
      <AskLink link={link} />
      <div className="opts">
        <div className={cx("opt", "other", answer && "on")}>
          <Textarea
            placeholder={optional ? "A note for the restart, if you want one" : "Your answer"}
            aria-label={optional ? "A note for the restart" : "Your answer"}
            value={answer}
            onChange={(event) => onAnswer(event.target.value)}
            rows={1}
            autosize
            className={OPTION_TEXTAREA}
          />
        </div>
      </div>
      <div className="qacts">
        <Btn variant="quiet" size="sm" onClick={onAsk}>
          Ask YAGNI
        </Btn>
      </div>
    </div>
  );
}
